import { motion, useInView, AnimatePresence } from 'framer-motion';
import { useRef, useState } from 'react';
import {
  SiHtml5, SiCss3, SiJavascript, SiPython, SiJupyter,
  SiMysql, SiTensorflow, SiPytorch, SiBootstrap, SiLaravel,
  SiFlask, SiFigma, SiArduino, SiC, SiGit, SiReact,
  SiPostgresql, SiOracle, SiDocker, SiLinux, SiOpencv,
  SiPhp, SiDart, SiFlutter, SiFastapi, SiMongodb,
  SiNodedotjs, SiVite, SiNestjs, SiSvelte,
} from 'react-icons/si';
import { FaJava, FaDatabase, FaTerminal } from 'react-icons/fa';
import { useLanguage } from '../contexts/LanguageContext';

const skillCategories = [
  {
    titleKey: 'AI / Machine Learning',
    skills: [
      { name: 'Python', icon: SiPython, color: '#3776AB' },
      { name: 'TensorFlow', icon: SiTensorflow, color: '#FF6F00' },
      { name: 'PyTorch', icon: SiPytorch, color: '#EE4C2C' },
      { name: 'OpenCV', icon: SiOpencv, color: '#5C3EE8' },
      { name: 'Jupyter', icon: SiJupyter, color: '#F37626' },
      { name: 'YOLO', icon: null, color: '#00D4AA', letter: 'Y' },
      { name: 'Roboflow', icon: null, color: '#A855F7', letter: 'R' },
    ],
  },
  {
    titleKey: 'Web & App Development',
    skills: [
      { name: 'HTML5', icon: SiHtml5, color: '#E34F26' },
      { name: 'CSS3', icon: SiCss3, color: '#1572B6' },
      { name: 'JavaScript', icon: SiJavascript, color: '#F7DF1E' },
      { name: 'Node.js', icon: SiNodedotjs, color: '#339933' },
      { name: 'Vite', icon: SiVite, color: '#646CFF' },
      { name: 'NestJS', icon: SiNestjs, color: '#E0234E' },
      { name: 'Svelte', icon: SiSvelte, color: '#FF3E00' },
      { name: 'React', icon: SiReact, color: '#61DAFB' },
      { name: 'PHP', icon: SiPhp, color: '#777BB4' },
      { name: 'Laravel', icon: SiLaravel, color: '#FF2D20' },
      { name: 'Flask', icon: SiFlask, color: '#44a5a5' },
      { name: 'FastAPI', icon: SiFastapi, color: '#009688' },
      { name: 'Bootstrap', icon: SiBootstrap, color: '#7952B3' },
      { name: 'Flutter', icon: SiFlutter, color: '#02569B' },
    ],
  },
  {
    titleKey: 'Database Administration',
    skills: [
      { name: 'MySQL', icon: SiMysql, color: '#4479A1' },
      { name: 'PostgreSQL', icon: SiPostgresql, color: '#4169E1' },
      { name: 'Oracle', icon: SiOracle, color: '#F80000' },
      { name: 'MongoDB', icon: SiMongodb, color: '#47A248' },
      { name: 'SQL Server', icon: FaDatabase, color: '#CC2927' },
    ],
  },
  {
    titleKey: 'Programming Languages',
    skills: [
      { name: 'Java', icon: FaJava, color: '#ED8B00' },
      { name: 'Dart', icon: SiDart, color: '#0175C2' },
      { name: 'C', icon: SiC, color: '#A8B9CC' },
    ],
  },
  {
    titleKey: 'DevOps & Tools',
    skills: [
      { name: 'Docker', icon: SiDocker, color: '#2496ED' },
      { name: 'Linux', icon: SiLinux, color: '#FCC624' },
      { name: 'Git', icon: SiGit, color: '#F05032' },
      { name: 'MobaXterm', icon: FaTerminal, color: '#5B6B8C' },
      { name: 'Figma', icon: SiFigma, color: '#F24E1E' },
      { name: 'Arduino', icon: SiArduino, color: '#00979D' },
    ],
  },
];

function SkillCard({ skill, index }) {
  const Icon = skill.icon;
  const [imgError, setImgError] = useState(false);

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.8 }}
      transition={{ duration: 0.2, delay: index * 0.03 }}
      whileHover={{ y: -5, scale: 1.05 }}
      className="group relative flex flex-col items-center gap-3 p-4 rounded-xl glass glass-hover cursor-default"
    >
      <div
        className="absolute inset-0 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-xl -z-10"
        style={{ backgroundColor: `${skill.color}15` }}
      />
      <div
        className="w-12 h-12 rounded-xl flex items-center justify-center transition-all duration-300 group-hover:shadow-lg"
        style={{ backgroundColor: `${skill.color}15` }}
      >
        {Icon ? (
          <Icon
            size={24}
            style={{ color: skill.color }}
            className="transition-transform duration-300 group-hover:scale-110"
          />
        ) : skill.image && !imgError ? (
          <img
            src={skill.image}
            alt={skill.name}
            className="w-6 h-6 object-contain transition-transform duration-300 group-hover:scale-110"
            onError={() => setImgError(true)}
          />
        ) : (
          <span className="text-lg font-bold" style={{ color: skill.color }}>
            {skill.letter}
          </span>
        )}
      </div>
      <span className="text-sm text-body font-medium group-hover:text-heading transition-colors text-center">
        {skill.name}
      </span>
    </motion.div>
  );
}

export default function Skills() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });
  const { t } = useLanguage();
  const [activeCategory, setActiveCategory] = useState(skillCategories[0].titleKey);

  const activeSkills = skillCategories.find(c => c.titleKey === activeCategory)?.skills || [];

  return (
    <section id="skills" className="relative py-24 overflow-hidden">
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-cyan-500/5 rounded-full blur-3xl" />
      <div className="absolute top-1/2 right-0 w-64 h-64 bg-accent-500/5 rounded-full blur-3xl" />

      <div className="section-container relative z-10" ref={ref}>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <p className="text-accent-400 font-mono text-sm mb-2">{t.skills.subtitle}</p>
          <h2 className="text-4xl sm:text-5xl font-bold text-heading mb-4">
            {t.skills.title} <span className="gradient-text">{t.skills.titleHighlight}</span>
          </h2>
          <div className="w-24 h-1 bg-linear-to-r from-accent-500 to-cyan-500 mx-auto rounded-full" />
        </motion.div>

        <div className="mb-10">
          <div className="tabs-scroll-wrap">
            <div className="tabs-scroll">
              <div className="tabs-scroll-inner glass p-1.5 rounded-xl mx-auto">
                {skillCategories.map((category) => (
                  <button
                    key={category.titleKey}
                    onClick={() => setActiveCategory(category.titleKey)}
                    className={`relative px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors duration-300 ${
                      activeCategory === category.titleKey
                        ? 'text-heading'
                        : 'text-muted hover:text-heading'
                    }`}
                  >
                    {activeCategory === category.titleKey && (
                      <motion.div
                        layoutId="activeSkillTab"
                        className="absolute inset-0 bg-accent-500/10 rounded-lg"
                        transition={{ type: 'spring', bounce: 0.2, duration: 0.6 }}
                      />
                    )}
                    <span className="relative z-10">{t.skills.categories[category.titleKey]}</span>
                  </button>
                ))}
              </div>
            </div>
            <div className="tabs-scroll-hint" aria-hidden="true">
              <span className="tabs-scroll-hint-thumb" />
            </div>
          </div>
        </div>

        <motion.div
          key={activeCategory}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.4 }}
          className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-7 gap-4"
        >
          <AnimatePresence>
            {activeSkills.map((skill, index) => (
              <SkillCard key={skill.name} skill={skill} index={index} />
            ))}
          </AnimatePresence>
        </motion.div>
      </div>
    </section>
  );
}
